import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "lucide-react";
import { useApartments } from "../hooks/useApartments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSmoobuIcal = ({ headers }) => {
  const APARTMENTS = useApartments();
  const [feeds, setFeeds] = useState({});
  const [saving, setSaving] = useState(false);

  const inputCls =
    "w-full bg-transparent border-b border-[#C29B62]/40 focus:border-[#1C3829] outline-none py-2.5 text-sm font-light placeholder:text-[#1A1A1A]/30 transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 mb-1";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/smoobu`, { headers: headers() });
      setFeeds(res.data.ical_feeds || {});
    } catch {
      /* parent already notifies */
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/smoobu/ical`, { feeds }, { headers: headers() });
      setFeeds(res.data.ical_feeds);
      toast.success("Link iCal salvati — ora premi «Sincronizza ora»");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 border border-[#C29B62]/20 bg-[#F7F5F0]/60 p-6" data-testid="smoobu-ical-section">
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-[#C29B62]" />
        <h3 className="text-xl font-light text-[#1C3829] font-serif-display">Sincronizzazione iCal (consigliata)</h3>
      </div>
      <p className="mt-2 text-xs text-[#1A1A1A]/50 leading-relaxed">
        In Smoobu apri ogni appartamento → Sincronizzazione → esportazione iCal, copia il link e incollalo qui sotto.
        Le prenotazioni Smoobu bloccheranno automaticamente le date sul sito. Lascia vuoto per scollegare.
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {APARTMENTS.map((a) => (
          <div key={a.id}>
            <label className={labelCls}>{a.name}</label>
            <input
              data-testid={`ical-input-${a.id}`}
              className={inputCls}
              placeholder="https://login.smoobu.com/ical/detail/…"
              value={feeds[a.id] || ""}
              onChange={(e) => setFeeds({ ...feeds, [a.id]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        data-testid="ical-save-button"
        onClick={save}
        disabled={saving}
        className="mt-6 bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:text-[#C29B62] transition-colors disabled:opacity-40"
      >
        Salva link iCal
      </button>

      <div className="mt-8 border-t border-[#C29B62]/20 pt-6" data-testid="ical-export-section">
        <h3 className="text-xl font-light text-[#1C3829] font-serif-display">Esporta verso Smoobu (senza chiavi API)</h3>
        <p className="mt-2 text-xs text-[#1A1A1A]/50 leading-relaxed">
          Per far arrivare su Smoobu le prenotazioni e i blocchi di questo sito: in Smoobu apri l'appartamento →
          Sincronizzazione → <strong>Importa iCal</strong> → incolla il link corrispondente qui sotto.
          Smoobu aggiornerà il calendario automaticamente a intervalli regolari.
        </p>
        <div className="mt-4 space-y-3">
          {APARTMENTS.map((a) => (
            <div key={a.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid={`ical-export-${a.id}`}>
              <span className="text-xs uppercase tracking-[0.15em] text-[#1A1A1A]/60 w-44 shrink-0">{a.name}</span>
              <code className="text-xs text-[#1C3829] break-all select-all">{`${process.env.REACT_APP_BACKEND_URL}/api/ical/${a.id}.ics`}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
